import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import {
  Avatar,
  AvatarComponent,
  IconComponent,
  Maybe,
  SELECTED_STATE_INJECTION_TOKEN,
  SkeletonComponent,
} from '@evc/web-components';

import { AuthService } from '../../../../clients/core/auth/service/auth.service';
import { OrganizationsService } from '../../../../clients/core/organizations/organizations.service';
import { UserService } from '../../../../clients/core/user/user.service';
import { I18nPlatformService } from '../../../i18n/i18n.service';
import {
  NAVIGATIONS_HOSTS,
  NAVIGATIONS_MODES,
  NavigationsHost,
  type NavigationsMode,
} from '../../types';

@Component({
  selector: 'evc-user-badge',
  imports: [
    AvatarComponent,
    IconComponent,
    SkeletonComponent,
  ],
  templateUrl: './user-badge.html',
  styleUrls: ['./user-badge.scss', './user-badge-ims.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-host]': 'host()',
    '[attr.data-layout]': 'layout()',
    '[attr.data-active]': 'selectedState()',
    '[attr.data-theme]': "isIms() ? 'ims' : null",
    '[attr.data-pending]': 'isPending()',
  },
  providers: [
    {
      provide: SELECTED_STATE_INJECTION_TOKEN,
      useExisting: UserBadgeComponent,
    },
  ],
})
export class UserBadgeComponent {
  layout = input<NavigationsMode>(NAVIGATIONS_MODES.PUBLIC);
  host = input<NavigationsHost>(NAVIGATIONS_HOSTS.TOPBAR);
  isIms = input(false);

  readonly #authService = inject(AuthService);
  readonly #organizationsService = inject(OrganizationsService);
  readonly #i18nPlatformService = inject(I18nPlatformService);
  readonly #userService = inject(UserService);

  protected readonly isPending = this.#authService.shouldPromptOrganizationSelection;

  protected readonly layouts = NAVIGATIONS_MODES;
  protected readonly currentOrganization = this.#organizationsService.current.bind(
    this.#organizationsService,
  );
  protected readonly userProfile = this.#userService.profile.bind(this.#userService);

  protected readonly i18n = {
    login: this.#i18nPlatformService.tSignal('menus.user.login'),
  };

  userAvatar = computed<Maybe<Avatar>>(() => this.userProfile()?.avatar);
  readonly selectedState = signal(false);
}
